import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Play, ClipboardCheck, AlertTriangle, Edit3, UserPlus, CalendarClock, X, Users, User, Library } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import TemplateLibraryDialog from "@/components/TemplateLibraryDialog";

const DOW = [
  { k: "mon", l: "Mon" }, { k: "tue", l: "Tue" }, { k: "wed", l: "Wed" },
  { k: "thu", l: "Thu" }, { k: "fri", l: "Fri" }, { k: "sat", l: "Sat" }, { k: "sun", l: "Sun" },
];

export default function Checklists() {
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tab, setTab] = useState("templates");
  const [assignDialog, setAssignDialog] = useState(null); // template being assigned
  const [assignments, setAssignments] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [t, r, a] = await Promise.all([
      api.get("/checklist-templates"),
      api.get("/checklist-runs"),
      api.get("/audit-assignments").catch(() => ({ data: [] })),
    ]);
    setTemplates(t.data); setRuns(r.data); setAssignments(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const startRun = async (tid) => {
    const r = await api.post("/checklist-runs", { template_id: tid, location: "" });
    nav(`/app/checklists/run/${r.data.id}`);
  };

  const assignmentsFor = (tid) => assignments.filter((a) => a.template_id === tid && a.active);

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex border border-[#1a1a1a]/30 bg-white rounded-full p-1">
          <button data-testid="tab-templates" onClick={() => setTab("templates")}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-full ${tab === "templates" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]"}`}>Templates</button>
          <button data-testid="tab-runs" onClick={() => setTab("runs")}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-full ${tab === "runs" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]"}`}>Runs</button>
          <button data-testid="tab-assignments" onClick={() => setTab("assignments")}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-full ${tab === "assignments" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]"}`}>Assignments</button>
        </div>
        {tab === "templates" && (
          <div className="flex gap-2">
            <button data-testid="ck-lib-button" onClick={() => setLibOpen(true)}
              className="bg-white text-[#1a1a1a] border border-[#1a1a1a]/20 px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#f7f5f2] inline-flex items-center gap-2 rounded-full">
              <Library className="w-3.5 h-3.5" /> Template library
            </button>
            <Link to="/app/checklists/builder" data-testid="new-template-button"
              className="bg-[#ff4500] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#cc3500] inline-flex items-center gap-2 rounded-full">
              <Plus className="w-3.5 h-3.5" /> New template
            </Link>
          </div>
        )}
      </div>

      {tab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 surface-card rounded-2xl py-16 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">No templates yet</div>
          )}
          {templates.map((t) => {
            const active = assignmentsFor(t.id);
            return (
              <div key={t.id} className="surface-card rounded-2xl p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex items-center gap-2 text-[#ff4500] mb-2">
                  <ClipboardCheck className="w-4 h-4" />
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em]">// Template</div>
                </div>
                <div className="font-display font-black text-lg leading-tight">{t.name}</div>
                <div className="text-[11px] font-mono text-[#1a1a1a]/60 uppercase tracking-wider mt-1">{t.items?.length || 0} items</div>
                {t.description && <div className="text-sm text-[#1a1a1a]/80 mt-2 leading-snug line-clamp-2">{t.description}</div>}
                {active.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`template-${t.id}-assignments`}>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 font-bold">Assigned:</span>
                    {active.slice(0, 3).map((a) => (
                      <span key={a.id} className="text-[9px] font-mono uppercase tracking-wider font-bold bg-[#d6a900]/20 text-[#a37e00] px-1.5 py-0.5 rounded-full">
                        {a.schedule_type === "once" ? "once" : a.schedule_type}
                      </span>
                    ))}
                    {active.length > 3 && <span className="text-[9px] text-[#1a1a1a]/50">+{active.length - 3}</span>}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid={`start-run-${t.id}`} onClick={() => startRun(t.id)}
                    className="inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold hover:bg-[#cc3500] rounded-full">
                    <Play className="w-3 h-3" /> Start run
                  </button>
                  <button data-testid={`assign-template-${t.id}`} onClick={() => setAssignDialog(t)}
                    className="inline-flex items-center gap-1.5 bg-[#d6a900] text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold hover:bg-[#a37e00] rounded-full">
                    <UserPlus className="w-3 h-3" /> Assign
                  </button>
                  <Link to={`/app/checklists/builder/${t.id}`} data-testid={`edit-template-${t.id}`}
                    className="inline-flex items-center gap-1.5 border border-[#1a1a1a] text-[#1a1a1a] px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150 rounded-full">
                    <Edit3 className="w-3 h-3" /> Edit
                  </Link>
                  {/* iter29 — delete removed from card; safer delete lives inside Edit page with a confirm dialog */}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "runs" && (
        <div className="surface-card rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-3 bg-[#1a1a1a] text-white text-[10px] uppercase tracking-[0.18em] font-bold">
            <div>Checklist</div><div>Location</div><div>Status</div><div>Fails</div><div></div>
          </div>
          {runs.length === 0 && <div className="py-10 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">No runs</div>}
          {runs.map((r) => {
            const fails = (r.items || []).filter((i) => i.status === "fail").length;
            return (
              <div key={r.id} className="grid grid-cols-[2fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-3 items-center border-b border-[#1a1a1a]/06 hover:bg-[#f0ede6] transition-colors duration-150">
                <div>
                  <div className="font-display font-bold text-sm">{r.template_name}</div>
                  <div className="text-[10px] font-mono text-[#1a1a1a]/60 uppercase tracking-wider">{new Date(r.started_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-[#1a1a1a]/80">{r.location || "—"}</div>
                <div>
                  <span className={`text-[10px] px-2 py-0.5 uppercase tracking-wider font-bold rounded-full ${r.status === "completed" ? "bg-[#1a8754] text-white" : "bg-[#1a1a1a] text-white"}`}>{r.status}</span>
                </div>
                <div>
                  {fails > 0 ? (
                    <span className="inline-flex items-center gap-1 bg-[#ff4500] text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full">
                      <AlertTriangle className="w-3 h-3" /> {fails}
                    </span>
                  ) : <span className="text-[#1a1a1a]/40">—</span>}
                </div>
                <Link to={`/app/checklists/run/${r.id}`} data-testid={`open-run-${r.id}`}
                  className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] hover:text-[#ff4500]">Open</Link>
              </div>
            );
          })}
        </div>
      )}

      {tab === "assignments" && (
        <AssignmentsList assignments={assignments} onChange={load} />
      )}

      <AssignAuditDialog open={!!assignDialog} template={assignDialog} onClose={() => setAssignDialog(null)}
        onSaved={() => { setAssignDialog(null); load(); }} />
      <TemplateLibraryDialog open={libOpen} onClose={() => setLibOpen(false)}
        kind="checklists" onInstalled={load} />
    </div>
  );
}

function AssignmentsList({ assignments, onChange }) {
  const [busy, setBusy] = useState(null);
  const del = async (aid) => {
    if (!window.confirm("Remove this assignment? Any completed runs will remain — this only stops the scheduled dispatch.")) return;
    setBusy(aid);
    try {
      await api.delete(`/audit-assignments/${aid}`);
      toast.success("Assignment removed");
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  if (assignments.length === 0) {
    return <div className="surface-card rounded-2xl py-16 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">No assignments yet. Click <strong>Assign</strong> on any template card.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assignments-list">
      {assignments.map((a) => (
        <div key={a.id} className="surface-card rounded-2xl p-4" data-testid={`assignment-${a.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#d6a900] text-white flex items-center justify-center"><CalendarClock className="w-4 h-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#a37e00] font-bold">{a.display_id}</div>
              <div className="font-display font-bold text-sm truncate">{a.template_name}</div>
            </div>
          </div>
          <div className="text-[11px] text-[#1a1a1a]/70 space-y-0.5">
            <div><strong>Schedule:</strong> {a.schedule_type}{a.schedule_type === "weekly" && a.schedule_days.length > 0 ? ` · ${a.schedule_days.join(", ")}` : ""}{a.schedule_time ? ` @ ${a.schedule_time}` : ""}</div>
            <div><strong>Starts:</strong> {a.start_date}{a.end_date ? ` · Ends: ${a.end_date}` : " · Open-ended"}</div>
            <div><strong>Assignees:</strong> {a.assignee_user_ids.length} user{a.assignee_user_ids.length === 1 ? "" : "s"}, {a.assignee_team_ids.length} hub{a.assignee_team_ids.length === 1 ? "" : "s"}</div>
          </div>
          {a.notes && <div className="text-[11px] italic text-[#1a1a1a]/60 mt-2 border-l-2 border-[#d6a900] pl-2">{a.notes}</div>}
          <div className="mt-3 flex gap-1">
            <button data-testid={`remove-assignment-${a.id}`} onClick={() => del(a.id)} disabled={busy === a.id}
              className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] hover:bg-[#ff4500] hover:text-white px-2 py-1 rounded-full inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AssignAuditDialog({ open, template, onClose, onSaved }) {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    assignee_user_ids: [], assignee_team_ids: [],
    schedule_type: "once", schedule_days: [], schedule_time: "",
    start_date: new Date().toISOString().slice(0, 10), end_date: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/teams").then(async (r) => {
      setTeams(r.data || []);
      const mems = await Promise.all((r.data || []).map((t) => api.get(`/teams/${t.id}/members`).then((m) => m.data).catch(() => [])));
      const seen = new Set(); const flat = [];
      mems.flat().forEach(({ user: u }) => { if (u && !seen.has(u.id)) { seen.add(u.id); flat.push(u); } });
      setUsers(flat);
    }).catch(() => {});
    setForm({
      assignee_user_ids: [], assignee_team_ids: [],
      schedule_type: "once", schedule_days: [], schedule_time: "",
      start_date: new Date().toISOString().slice(0, 10), end_date: "",
      notes: "",
    });
  }, [open]);

  const toggle = (key, id) => {
    const arr = form[key];
    setForm({ ...form, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };

  const submit = async () => {
    if (!template) return;
    if (form.assignee_user_ids.length === 0 && form.assignee_team_ids.length === 0) {
      toast.error("Pick at least one user or hub"); return;
    }
    setBusy(true);
    try {
      await api.post("/audit-assignments", { template_id: template.id, active: true, ...form });
      toast.success(`Assigned ${template.name}`);
      onSaved?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl border-[#1a1a1a]/15 max-h-[92vh] overflow-y-auto scrollbar-thin" data-testid="assign-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#d6a900]" /> Assign audit: {template?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold"><User className="w-3 h-3 inline mr-1" /> Assign to people ({form.assignee_user_ids.length})</label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-[#1a1a1a]/10 rounded-md bg-white max-h-32 overflow-auto scrollbar-thin">
              {users.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No teammates found — join a hub first.</span>}
              {users.map((u) => {
                const on = form.assignee_user_ids.includes(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggle("assignee_user_ids", u.id)}
                    data-testid={`assign-user-${u.id}`}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {u.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold"><Users className="w-3 h-3 inline mr-1" /> Assign to hubs ({form.assignee_team_ids.length}) — everyone in the hub gets their own run</label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-[#1a1a1a]/10 rounded-md bg-white max-h-32 overflow-auto scrollbar-thin">
              {teams.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No hubs yet.</span>}
              {teams.map((t) => {
                const on = form.assignee_team_ids.includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggle("assignee_team_ids", t.id)}
                    data-testid={`assign-team-${t.id}`}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#ff4500] border-[#ff4500]/30"}`}>
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">How often?</label>
              <select value={form.schedule_type} onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
                data-testid="assign-schedule-type"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm">
                <option value="once">Once (specific date)</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly (pick days)</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Time (optional — blank = anytime that day)</label>
              <input type="time" value={form.schedule_time} onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                data-testid="assign-schedule-time"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm" />
            </div>
          </div>

          {form.schedule_type === "weekly" && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Which days of the week?</label>
              <div className="flex flex-wrap gap-1">
                {DOW.map((d) => {
                  const on = form.schedule_days.includes(d.k);
                  return (
                    <button key={d.k} type="button" onClick={() => setForm({ ...form, schedule_days: on ? form.schedule_days.filter((x) => x !== d.k) : [...form.schedule_days, d.k] })}
                      data-testid={`assign-dow-${d.k}`}
                      className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#d6a900] text-white border-[#d6a900]" : "bg-white text-[#1a1a1a]/70 border-[#1a1a1a]/15"}`}>
                      {d.l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Start date *</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="assign-start-date"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">End date (optional)</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="assign-end-date"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Notes (optional — helpful reminder)</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} data-testid="assign-notes"
              placeholder="e.g. Line Centric Team must complete Supervisor Floor Round every day Mon–Fri before 07:30."
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="assign-submit" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary"><UserPlus className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Assign audit"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
